import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarClock, Upload, Sparkles, UserPlus, Send, Trash2,
  ExternalLink, Loader2, FileText, X,
} from "lucide-react";

/**
 * Maturing-loan pipeline
 * ----------------------
 * Broker drops a PDF or CSV export from their title company → we parse,
 * de-dupe, run a Tier-1 CRM match on import, then let them:
 *  • Enrich (Perplexity) — LLC → managing member + best-effort contact
 *  • Promote to Contact — creates a real CRM row
 *  • Enroll in Campaign — piggybacks on the existing Campaigns module
 *  • Dismiss — hides the row without deleting the underlying record
 *
 * All row actions are inline; no route changes required.
 */
export default function AdminMaturity() {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [quarterFilter, setQuarterFilter] = useState("");
  const [enriching, setEnriching] = useState({}); // pid -> true
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [campaignMenu, setCampaignMenu] = useState(null); // pid
  const fileInput = useRef(null);
  const [importing, setImporting] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (quarterFilter) params.quarter = quarterFilter;
    api.get("/admin/maturity", { params })
      .then((r) => setProspects(r.data.prospects || []))
      .catch(() => setProspects([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [statusFilter, quarterFilter]);   // eslint-disable-line
  useEffect(() => {
    api.get("/admin/campaigns")
      .then((r) => setActiveCampaigns((r.data.campaigns || [])
        .filter((c) => c.status === "active")))
      .catch(() => setActiveCampaigns([]));
  }, []);

  const quarters = useMemo(() => {
    const s = new Set();
    prospects.forEach((p) => p.maturity_quarter && s.add(p.maturity_quarter));
    return [...s].sort();
  }, [prospects]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    try {
      const r = await api.post("/admin/maturity/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = r.data;
      toast.success(
        `Imported ${d.imported} of ${d.total_rows} rows`
        + (d.crm_matches ? ` — ${d.crm_matches} matched your CRM` : "")
      );
      if (d.warnings?.length) toast.warning(d.warnings[0]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const enrich = async (p) => {
    setEnriching((s) => ({ ...s, [p.id]: true }));
    try {
      const r = await api.post(`/admin/maturity/${p.id}/enrich`);
      if (r.data.status === "enriched" || r.data.status === "crm_match") {
        toast.success(`Enriched: ${r.data.contact?.person || "match found"}`);
      } else {
        toast.warning("No luck — try adding to CRM manually.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrichment failed");
    } finally {
      setEnriching((s) => ({ ...s, [p.id]: false }));
    }
  };

  const promote = async (p) => {
    try {
      const r = await api.post(`/admin/maturity/${p.id}/promote-to-contact`, {});
      toast.success(`Contact created: ${r.data.contact.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't promote");
    }
  };

  const enroll = async (p, campaignId) => {
    if (!p.linked_contact_id) {
      try {
        const r = await api.post(`/admin/maturity/${p.id}/promote-to-contact`, {});
        p.linked_contact_id = r.data.contact.id;
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Promote first");
        setCampaignMenu(null);
        return;
      }
    }
    try {
      const r = await api.post(`/admin/campaigns/${campaignId}/enroll`, {
        subjects: [{ subject_type: "contact", subject_id: p.linked_contact_id }],
      });
      const camp = activeCampaigns.find((c) => c.id === campaignId);
      if (r.data.enrolled) toast.success(`Enrolled in "${camp?.name}"`);
      else if (r.data.already_enrolled) toast.info("Already enrolled");
      else toast.warning(r.data.results?.[0]?.reason || "Enrollment skipped");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enrollment failed");
    } finally {
      setCampaignMenu(null);
      load();
    }
  };

  const dismiss = async (p) => {
    if (!window.confirm(`Hide ${p.owner_llc || p.property_address}?`)) return;
    try {
      await api.post(`/admin/maturity/${p.id}/dismiss`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't dismiss");
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-maturity-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#6B6558]">
            // Broker prospecting
          </div>
          <h1 className="font-serif text-3xl mt-1 flex items-center gap-2">
            <CalendarClock size={22} /> Maturing Loans
          </h1>
          <p className="text-sm text-[#6B6558] mt-1 max-w-xl">
            Drop a PDF or CSV from your title company. We&apos;ll parse it,
            match against your CRM, and enrich each LLC with the decision-maker
            via Perplexity. Then promote to Contacts and enroll in a campaign.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInput}
            type="file"
            accept=".csv,.pdf,application/pdf,text/csv"
            data-testid="maturity-upload-input"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <button
            onClick={() => fileInput.current?.click()}
            disabled={importing}
            data-testid="maturity-upload-btn"
            className="byrd-btn byrd-btn-dark"
          >
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Upload title-co report
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="maturity-status-filter"
          className="h-9 px-2 rounded-md border border-[#E4DFD1] bg-white text-sm">
          <option value="">All statuses</option>
          <option value="pending">Pending enrichment</option>
          <option value="crm_match">CRM match</option>
          <option value="enriched">Enriched</option>
          <option value="failed">Failed</option>
        </select>
        <select value={quarterFilter} onChange={(e) => setQuarterFilter(e.target.value)}
          data-testid="maturity-quarter-filter"
          className="h-9 px-2 rounded-md border border-[#E4DFD1] bg-white text-sm">
          <option value="">All maturities</option>
          {quarters.map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
        <div className="text-xs text-[#6B6558] self-center ml-auto">
          {prospects.length} record{prospects.length === 1 ? "" : "s"}
        </div>
      </div>

      {loading ? (
        <div className="byrd-card p-8 text-center text-[#6B6558]">Loading…</div>
      ) : prospects.length === 0 ? (
        <EmptyState onUpload={() => fileInput.current?.click()} />
      ) : (
        <div className="byrd-card overflow-hidden">
          <div className="grid grid-cols-[1.4fr_1.2fr_0.8fr_0.9fr_1.3fr_190px] gap-2 px-4 py-2 bg-[#F3EEE0] text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">
            <div>Property</div>
            <div>Owner LLC</div>
            <div>Maturity</div>
            <div>Balance</div>
            <div>Contact</div>
            <div className="text-right">Actions</div>
          </div>
          {prospects.map((p) => (
            <ProspectRow key={p.id} p={p}
              onEnrich={enrich} onPromote={promote} onDismiss={dismiss}
              enriching={enriching[p.id]}
              campaigns={activeCampaigns}
              openCampaignMenu={campaignMenu === p.id}
              onToggleCampaign={() => setCampaignMenu(campaignMenu === p.id ? null : p.id)}
              onEnroll={enroll}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onUpload }) {
  return (
    <div className="byrd-card p-10 text-center">
      <FileText size={28} className="mx-auto text-[#6B6558] mb-3" />
      <div className="font-serif text-xl mb-1">No maturity reports yet</div>
      <div className="text-sm text-[#6B6558] mb-4 max-w-md mx-auto">
        Upload a title-company report (PDF or CSV). We&apos;ll parse
        property address, owner LLC, maturity date, and lender.
      </div>
      <button onClick={onUpload} data-testid="maturity-empty-upload"
        className="byrd-btn byrd-btn-dark">
        <Upload size={14} /> Upload your first report
      </button>
    </div>
  );
}

function ProspectRow({ p, onEnrich, onPromote, onDismiss, enriching,
                       campaigns, openCampaignMenu, onToggleCampaign, onEnroll }) {
  const status = p.enrichment_status;
  const c = p.enriched_contact || {};
  const statusPill = {
    pending:   { text: "Pending",   cls: "bg-[#F3EEE0] text-[#6B6558]" },
    crm_match: { text: "CRM match", cls: "bg-[#DFF0DF] text-[#245C25]" },
    enriched:  { text: "Enriched",  cls: "bg-[#E4E2FA] text-[#3D3A8C]" },
    failed:    { text: "Failed",    cls: "bg-[#FADCDA] text-[#8A1F1A]" },
  }[status] || { text: status, cls: "bg-[#F3EEE0] text-[#6B6558]" };

  return (
    <div className="grid grid-cols-[1.4fr_1.2fr_0.8fr_0.9fr_1.3fr_190px] gap-2 px-4 py-3 border-t border-[#E4DFD1] items-center hover:bg-[#FBF8F1]"
      data-testid={`maturity-row-${p.id}`}>
      <div>
        <div className="text-sm text-[#1A1A1A]">{p.property_address || "—"}</div>
        <div className="text-[11px] text-[#6B6558]">
          {[p.property_city, p.property_state, p.property_zip].filter(Boolean).join(", ") || "\u00A0"}
        </div>
      </div>
      <div>
        <div className="text-sm text-[#1A1A1A]">{p.owner_llc || "—"}</div>
        <div className="text-[11px] text-[#6B6558]">{p.current_lender || ""}</div>
      </div>
      <div className="text-sm">
        <div>{p.maturity_date || "—"}</div>
        <div className="text-[10px] font-mono text-[#8A8471]">{p.maturity_quarter || ""}</div>
      </div>
      <div className="text-sm">
        {p.loan_amount
          ? `$${Math.round(p.loan_amount).toLocaleString()}`
          : "—"}
      </div>
      <div className="text-xs">
        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-mono ${statusPill.cls}`}>
          {statusPill.text}
        </span>
        {c.person && (
          <div className="mt-1 text-[#1A1A1A]">{c.person}</div>
        )}
        {(c.email || c.phone) && (
          <div className="text-[11px] text-[#6B6558]">
            {[c.email, c.phone].filter(Boolean).join(" · ")}
          </div>
        )}
        {c.linkedin_url && (
          <a href={c.linkedin_url} target="_blank" rel="noreferrer"
             className="text-[11px] text-[#3D3A8C] inline-flex items-center gap-0.5">
            LinkedIn <ExternalLink size={9} />
          </a>
        )}
      </div>
      <div className="flex justify-end items-start gap-1 relative">
        {(status === "pending" || status === "failed") && (
          <button onClick={() => onEnrich(p)} disabled={enriching}
            className="text-xs px-2.5 py-1 rounded-full border border-[#D6CDB8] hover:bg-[#F3EEE0]"
            data-testid={`enrich-${p.id}`} title="Look up decision-maker via Perplexity">
            {enriching ? <Loader2 size={11} className="animate-spin" /> : <><Sparkles size={11} /> Enrich</>}
          </button>
        )}
        {(status === "enriched" || status === "crm_match") && !p.linked_contact_id && (
          <button onClick={() => onPromote(p)}
            className="text-xs px-2.5 py-1 rounded-full border border-[#D6CDB8] hover:bg-[#F3EEE0]"
            data-testid={`promote-${p.id}`} title="Create a CRM contact from this row">
            <UserPlus size={11} /> Add to CRM
          </button>
        )}
        {campaigns.length > 0 && (
          <div className="relative">
            <button onClick={onToggleCampaign}
              className="text-xs px-2.5 py-1 rounded-full border border-[#D6CDB8] hover:bg-[#F3EEE0]"
              data-testid={`enroll-${p.id}`}>
              <Send size={11} />
            </button>
            {openCampaignMenu && (
              <div className="absolute right-0 top-8 z-30 min-w-[240px] bg-white border border-[#E4DFD1] rounded-md shadow-lg py-1 max-h-72 overflow-y-auto">
                <div className="px-3 py-1 text-[10px] uppercase tracking-widest text-[#6B6558] font-mono border-b border-[#F3EEE0]">
                  Enroll in campaign
                </div>
                {campaigns.map((camp) => (
                  <button key={camp.id} onClick={() => onEnroll(p, camp.id)}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-[#FBF8F1]"
                    data-testid={`enroll-campaign-${p.id}-${camp.id}`}>
                    {camp.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <button onClick={() => onDismiss(p)}
          className="text-xs w-7 h-7 grid place-items-center rounded-full text-[#6B6558] hover:text-[#8A1F1A] hover:bg-[#FADCDA]"
          data-testid={`dismiss-${p.id}`} title="Hide">
          <X size={11} />
        </button>
      </div>
    </div>
  );
}
